/**
 * Individual Blog Post Viewer for ShopKaBot
 * Redesigned with a Medium-inspired reading layout.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Seo from "../../seo/Seo";
import { createArticleSchema, createBreadcrumbSchema, createOrganizationSchema } from "../../seo/utils";
import blogsData from "../../data/blogs.json";
import ShopKaBotLeadForm from "../Shared/LeadForm";

interface Blog {
  id: number;
  slug: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  image: string;
  publishedOn: string;
  modifiedOn: string;
  readingTime: number;
  body: string;
  cta: string;
  targetKeyword: string;
}

const formatArticleDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

function BlogViewerPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [hideImage, setHideImage] = useState(false);

  const blog = useMemo(
    () => blogsData.blogs.find((item) => item.slug === slug) as Blog | undefined,
    [slug]
  );

  const relatedBlogs = useMemo(() => {
    if (!blog) return [];

    return blogsData.blogs
      .filter(
        (item) =>
          item.id !== blog.id &&
          (item.category === blog.category || item.tags.some((tag) => blog.tags.includes(tag)))
      )
      .slice(0, 3);
  }, [blog]);

  useEffect(() => {
    setHideImage(false);
  }, [slug]);

  if (!blog) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h5" color="textSecondary">
          Article not found
        </Typography>
        <Button onClick={() => navigate("/blogs")} sx={{ mt: 2 }}>
          Back to Blog
        </Button>
      </Box>
    );
  }

  const schema = [
    createArticleSchema({
      title: blog.title,
      description: blog.description,
      body: blog.body,
      path: `/blogs/${blog.slug}`,
      publishedTime: new Date(blog.publishedOn).toISOString(),
      modifiedTime: new Date(blog.modifiedOn).toISOString(),
      image: blog.image,
    }),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blogs" },
      { name: blog.title, path: `/blogs/${blog.slug}` },
    ]),
    createOrganizationSchema(),
  ];

  return (
    <>
      <Seo
        title={`${blog.title} | ShopKaBot Blog`}
        description={blog.description}
        path={`/blogs/${blog.slug}`}
        keywords={[...blog.tags, blog.targetKeyword, "ShopKaBot"]}
        image={blog.image}
        imageAlt={blog.title}
        type="article"
        schema={schema}
        publishedTime={new Date(blog.publishedOn).toISOString()}
        modifiedTime={new Date(blog.modifiedOn).toISOString()}
      />

      <Box sx={{ minHeight: "100vh", bgcolor: "#ffffff" }}>
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
          <Grid container spacing={{ xs: 4, lg: 6 }}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Box sx={{ maxWidth: 760, mx: "auto" }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/blogs")}
                  sx={{ mb: 3, color: "#4b5563", display: { xs: "inline-flex", sm: "none" } }}
                >
                  Back to blogs
                </Button>

                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 800,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "#166534",
                    mb: 1.5,
                  }}
                >
                  {blog.category} / ShopKaBot Journal
                </Typography>

                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: "2.15rem", md: "4.3rem" },
                    lineHeight: { xs: 1.1, md: 0.97 },
                    letterSpacing: "-0.055em",
                    fontWeight: 800,
                    color: "#111827",
                    mb: 2,
                  }}
                >
                  {blog.title}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: "1.08rem", md: "1.35rem" },
                    lineHeight: 1.75,
                    color: "#4b5563",
                    mb: 4,
                    maxWidth: 720,
                  }}
                >
                  {blog.description}
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  sx={{ mb: 4 }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: "#111827", width: 48, height: 48 }}>SK</Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                        ShopKaBot Editorial
                      </Typography>
                      <Typography sx={{ color: "#6b7280", fontSize: "0.94rem" }}>
                        Playbooks and field notes for teams automating customer conversations.
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={2.5} alignItems="center" sx={{ color: "#6b7280", flexWrap: "wrap", gap: 1 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <CalendarMonthIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2">{formatArticleDate(blog.publishedOn)}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <AccessTimeIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2">{blog.readingTime} min read</Typography>
                    </Stack>
                  </Stack>
                </Stack>

                {blog.image && !hideImage ? (
                  <Box
                    component="img"
                    src={blog.image}
                    alt={blog.title}
                    onError={() => setHideImage(true)}
                    sx={{
                      width: "100%",
                      borderRadius: 5,
                      maxHeight: 420,
                      objectFit: "cover",
                      boxShadow: "0 28px 60px rgba(15, 23, 42, 0.12)",
                      mb: 5,
                    }}
                  />
                ) : null}

                <Box
                  className="blog-content"
                  sx={{
                    color: "#1f2937",
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    "& h2": {
                      mt: 5,
                      mb: 2,
                      fontSize: { xs: "1.7rem", md: "2rem" },
                      lineHeight: 1.2,
                      fontWeight: 700,
                      color: "#111827",
                      fontFamily: 'Georgia, "Times New Roman", serif',
                    },
                    "& h3": {
                      mt: 4,
                      mb: 1.5,
                      fontSize: { xs: "1.3rem", md: "1.45rem" },
                      lineHeight: 1.25,
                      fontWeight: 700,
                      color: "#111827",
                      fontFamily: 'Georgia, "Times New Roman", serif',
                    },
                    "& p": {
                      mb: 2.5,
                      fontSize: { xs: "1.1rem", md: "1.22rem" },
                      lineHeight: 1.95,
                      color: "#374151",
                    },
                    "& ul, & ol": {
                      mb: 3,
                      pl: 3,
                    },
                    "& li": {
                      mb: 1.4,
                      fontSize: { xs: "1.06rem", md: "1.16rem" },
                      lineHeight: 1.9,
                      color: "#374151",
                    },
                    "& strong": {
                      color: "#111827",
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: blog.body }}
                />

                <Divider sx={{ my: 5 }} />

                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 4 }}>
                  {blog.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      variant="outlined"
                      sx={{
                        borderRadius: "999px",
                        bgcolor: "#f9fafb",
                      }}
                    />
                  ))}
                </Stack>

                <Paper
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    mb: 4,
                    borderRadius: 4,
                    bgcolor: "#f8fafc",
                    border: "1px solid rgba(17, 24, 39, 0.08)",
                  }}
                >
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#166534", mb: 1 }}>
                    Next Step
                  </Typography>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, lineHeight: 1.25, color: "#111827", mb: 1.25 }}>
                    {blog.cta}
                  </Typography>
                  <Typography sx={{ color: "#4b5563", lineHeight: 1.8, mb: 2 }}>
                    If this article matches what your team is trying to solve, share your
                    requirements and we can help turn it into a working WhatsApp automation flow.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/contact-us")}
                    sx={{
                      borderRadius: "999px",
                      px: 2.25,
                      bgcolor: "#111827",
                      "&:hover": { bgcolor: "#000000" },
                    }}
                  >
                    Fill Lead Form
                  </Button>
                </Paper>

                <Paper
                  sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 4,
                    bgcolor: "#ffffff",
                    border: "1px solid rgba(17, 24, 39, 0.08)",
                  }}
                >
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#6b7280", mb: 1.5 }}>
                    About This Article
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: "#6b7280" }}>Topic</Typography>
                      <Typography sx={{ fontWeight: 700, color: "#111827" }}>{blog.category}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: "#6b7280" }}>Read time</Typography>
                      <Typography sx={{ fontWeight: 700, color: "#111827" }}>{blog.readingTime} min</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: "#6b7280" }}>Published</Typography>
                      <Typography sx={{ fontWeight: 700, color: "#111827" }}>{formatArticleDate(blog.publishedOn)}</Typography>
                    </Stack>
                  </Stack>
                </Paper>

                <Paper
                  sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 4,
                    bgcolor: "#f8fafc",
                    border: "1px solid rgba(17, 24, 39, 0.08)",
                  }}
                >
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#6b7280", mb: 2 }}>
                    Work With Us
                  </Typography>
                  <Typography sx={{ fontSize: "1.2rem", fontWeight: 800, lineHeight: 1.3, color: "#111827", mb: 1.25 }}>
                    Turn article ideas into live automation systems.
                  </Typography>
                  <Typography sx={{ color: "#4b5563", lineHeight: 1.8, mb: 2 }}>
                    We design WhatsApp-first support, sales, and booking agents for growing teams.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate("/contact-us")}
                    sx={{
                      borderRadius: "999px",
                      bgcolor: "#111827",
                      "&:hover": { bgcolor: "#000000" },
                    }}
                  >
                    Fill Lead Form
                  </Button>
                </Paper>

                {relatedBlogs.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 4,
                      bgcolor: "#ffffff",
                      border: "1px solid rgba(17, 24, 39, 0.08)",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#6b7280", mb: 2 }}>
                      Related Articles
                    </Typography>

                    <Stack divider={<Divider />}>
                      {relatedBlogs.map((relatedBlog) => (
                        <Box
                          key={relatedBlog.id}
                          onClick={() => navigate(`/blogs/${relatedBlog.slug}`)}
                          sx={{
                            py: 2,
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                            "&:hover": {
                              transform: "translateX(4px)",
                            },
                          }}
                        >
                          <Typography sx={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#166534", mb: 1 }}>
                            {relatedBlog.category}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, color: "#111827", lineHeight: 1.35, mb: 0.75 }}>
                            {relatedBlog.title}
                          </Typography>
                          <Typography sx={{ color: "#6b7280", lineHeight: 1.7, fontSize: "0.94rem" }}>
                            {relatedBlog.description}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              <Stack spacing={3} sx={{ position: { lg: "sticky" }, top: { lg: 98 } }}>
                <Box>
                  <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#111827", mb: 2 }}>
                    Ready to automate your business?
                  </Typography>
                  <ShopKaBotLeadForm formSize="md" />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export default BlogViewerPage;
